import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid2";
import EventCard from "../../components/EventCard";
import InnerLayout from "../../layouts/InnerLayout";
import { Container, Box, Tabs, Tab, Typography } from "@mui/material";
import InnerBanner from "../../images/inner-banner.jpg";
import CallforAction from "../../components/call_for_action/CallForActtion";
import apiService from "../../services/api.js"; // Ensure this path is correct

const parseDate = (dateString) => {
  return new Date(dateString);
};

const filterAndSortEvents = (events, category) => {
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  // Filter events based on category
  let filteredEvents = events.filter((event) => {
    const startDate = parseDate(event.start_date_time);
    const endDate = event.end_date_time ? parseDate(event.end_date_time) : null;

    if (category === "upcoming") {
      return startDate > now; // Only upcoming events
    } else if (category === "ongoing") {
      if (endDate) {
        return startDate <= now && endDate >= now;
      } else {
        const eventDay = new Date(startDate);
        eventDay.setHours(0, 0, 0, 0);
        return eventDay.getTime() === today.getTime();
      }
    } else if (category === "past") {
      if (endDate) {
        return endDate < now;
      } else {
        return startDate < today;
      }
    }
    return true; // For 'all' category
  });

  // Sort events: upcoming first (closest first), then ongoing, then past (most recent first)
  filteredEvents.sort((a, b) => {
    const startA = parseDate(a.start_date_time);
    const startB = parseDate(b.start_date_time);
    const endA = a.end_date_time ? parseDate(a.end_date_time) : null;
    const endB = b.end_date_time ? parseDate(b.end_date_time) : null;

    // Check if events are upcoming (in the future)
    const isUpcomingA = startA > now;
    const isUpcomingB = startB > now;
    
    // Check if events are ongoing
    const isOngoingA = !isUpcomingA && ((endA && endA >= now) || 
                      (!endA && new Date(startA).toDateString() === now.toDateString()));
    const isOngoingB = !isUpcomingB && ((endB && endB >= now) || 
                      (!endB && new Date(startB).toDateString() === now.toDateString()));

    // Upcoming events come first (closest first)
    if (isUpcomingA && !isUpcomingB) return -1;
    if (!isUpcomingA && isUpcomingB) return 1;
    if (isUpcomingA && isUpcomingB) return startA - startB;
    
    // Then ongoing events
    if (isOngoingA && !isOngoingB) return -1;
    if (!isOngoingA && isOngoingB) return 1;
    
    // Default sort by start date (ascending)
    return startA - startB;
  });

  return filteredEvents;
};

function Events() {
  const [value, setValue] = useState(0);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService
      .getEvents()
      .then((response) => {
        if (response.data.success && response.data.data.length > 0) {
          // Include category name in each event
          const allEvents = response.data.data.flatMap((category) =>
            category.events.map((event) => ({
              ...event,
              category_name: category.name, // Add the category name to each event
            }))
          );
          setEvents(allEvents);
        } else {
          setEvents([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load events.");
        setLoading(false);
      });
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  if (loading) return <Typography>Loading events...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  const upcomingEvents = filterAndSortEvents(events, "upcoming");
  const ongoingEvents = filterAndSortEvents(events, "ongoing");
  const pastEvents = filterAndSortEvents(events, "past");
  const allEvents = [
    ...filterAndSortEvents(events, "upcoming"), // Add upcoming events first
    ...filterAndSortEvents(events, "ongoing"), // Then ongoing events
    ...filterAndSortEvents(events, "past").reverse(), // Add past events in reverse order
  ];

  return (
    <InnerLayout>
      <Box component="section" className="inner-banner">
        <Box
          component="img"
          src={InnerBanner}
          className="banner-pic"
          alt="Events Banner"
        />
        <Box className="inner-text">
          <Container maxWidth="xl">
            <Grid
              container
              spacing={2}
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <Grid item md={6}>
                <Typography
                  component="h1"
                  variant="h1"
                  className="section-title text-white"
                >
                  Events
                </Typography>
              </Grid>
              <Grid item md={6}>
                <CallforAction />
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      <Box component="section" className="event-page">
        <Container maxWidth="xl">
          <Box className="mobile-grid" sx={{ flexGrow: 1, display: "flex", height: "auto" }}>
            <Tabs
              orientation="vertical"
              variant="scrollable"
              className="all-events-tabs"
              value={value}
              onChange={handleChange}
              aria-label="Event Categories"
              sx={{ borderRight: 1, borderColor: "divider", minWidth: 200 }}
            >
              <Tab label="All Events" />
              <Tab label="Upcoming Events" />
              <Tab label="Ongoing Events" />
              <Tab label="Past Events" />
            </Tabs>

            <Box sx={{ flexGrow: 1, p: 3 }}>
              {value === 0 && <EventGrid events={allEvents} sortDescending={false} />}
              {value === 1 && <EventGrid events={upcomingEvents} sortDescending={false} />}
              {value === 2 && <EventGrid events={ongoingEvents} sortDescending={false} />}
              {value === 3 && <EventGrid events={pastEvents} sortDescending={true} />}
            </Box>
          </Box>
        </Container>
      </Box>
    </InnerLayout>
  );
}

const EventGrid = ({ events, sortDescending }) => {
  // Events are already sorted in the filterAndSortEvents function
  // We just need to reverse the order for past events (most recent first)
  const sortedEvents = sortDescending ? [...events].reverse() : [...events];

  return (
    <Grid container spacing={2}>
      {sortedEvents.length > 0 ? (
        sortedEvents.map((event, index) => (
          <Grid item size={{ md: 4 }} key={index}>
            <EventCard event={event} />
          </Grid>
        ))
      ) : (
        <Typography>No events available</Typography>
      )}
    </Grid>
  );
};

export default Events;